import { useQuery } from '@tanstack/react-query';
import { authHeaders } from '@/api/persist';
import { queryClient } from '@/api/queryClient';
import { useBrainStore } from '@/state/brainStore';
// Self-reference so `queryFn`s call the module's *exported* functions rather
// than closing over their local bindings — that's what lets a test's
// `vi.spyOn(views, 'fetchCalendarView')` intercept calls made from inside
// this module (e.g. from useCalendarView's own queryFn), not just calls made
// by external importers.
import * as self from '@/api/views';
import { isStoreFallbackEnabled } from '@/api/apiMode';
import { ALL_STUDIOS_SCOPE } from '@/domain/studioScope';
import { useConnectionStatus } from '@/state/connectionStatus';
import type { SequenceState } from '@/domain/sequenceFlow';
import type {
  Artist,
  Booking,
  ConfirmationCall,
  CustomerRecord,
  Deposit,
  MerchItem,
  NoDepositDeclineFlag,
  NoRebookFlag,
  OverrideRequest,
  PaybyExtensionRequest,
  PayoutRecord,
  Piece,
  SalePayment,
  Studio,
  StudioId,
  TopupPlan,
  Waiver,
} from '@/domain/types';
import { isCommittedBooking } from '@atink/domain';
import { lastUnansweredMeta, pushedCustomerIdsOf, type OpsCustomerBookingLite } from '@/domain/opsWorklist';

/**
 * Live per-view reads for the calendar: narrower, per-studio + per-date-range
 * slices of the world (GET /api/views/calendar, GET /api/views/availability)
 * shaped identically to the /api/bootstrap bundle, so the client's existing
 * derivation logic can consume either without knowing which endpoint served
 * it. `calendarViewFromStore` is the offline fallback — it derives the same
 * shape from the already-hydrated `brainStore` when the endpoint is
 * unreachable (or errors), so a flaky connection degrades to "last known
 * good" rather than an empty calendar.
 */

export interface CalendarViewBundle {
  bookings: Record<string, Booking>;
  customers: Record<string, CustomerRecord>;
  deposits: Record<string, Deposit>;
  waivers: Record<string, Waiver>;
  overrideRequests: Record<string, OverrideRequest>;
  artists: Record<string, Artist>;
  studios: Record<string, Studio>;
}

export interface AvailabilityViewBundle {
  bookings: Record<string, Booking>;
  artists: Record<string, Artist>;
  studios: Record<string, Studio>;
}

export interface DepositsViewBundle {
  deposits: Record<string, Deposit>;
  bookings: Record<string, Booking>;
  customers: Record<string, CustomerRecord>;
  studios: Record<string, Studio>;
  artists: Record<string, Artist>;
}

export interface ConfirmationsViewBundle {
  bookings: Record<string, Booking>;
  confirmationCalls: Record<string, ConfirmationCall>;
  customers: Record<string, CustomerRecord>;
  deposits: Record<string, Deposit>;
  waivers: Record<string, Waiver>;
  artists: Record<string, Artist>;
  studios: Record<string, Studio>;
}

export interface OpsViewBundle {
  bookings: Record<string, Booking>;
  overrideRequests: Record<string, OverrideRequest>;
  topupPlans: Record<string, TopupPlan>;
  noRebookFlags: Record<string, NoRebookFlag>;
  noDepositDeclineFlags: Record<string, NoDepositDeclineFlag>;
  payoutRecords: Record<string, PayoutRecord>;
  customers: Record<string, CustomerRecord>;
  /** W5 mg: open unpaid deposit links (pending / expired, not closed off, not re-sent). */
  deposits: Record<string, Deposit>;
  /** W5 i5: pay-by extension requests. */
  paybyExtensionRequests: Record<string, PaybyExtensionRequest>;
  /** B1 (8tqr): the sales Unpaid-artists rows are priced from. Merch lines not loaded. */
  salePayments: Record<string, SalePayment>;
  artists: Record<string, Artist>;
  studios: Record<string, Studio>;
  /** B8 (N8): customers pushed to the Agent — never "Unanswered" for the Messenger. */
  pushedCustomerIds?: string[];
  /** tv-3zjo: these customers' booked sessions at any studio (skips a booked client's DM). */
  customerBookings?: OpsCustomerBookingLite[];
}

export interface FloorViewBundle {
  bookings: Record<string, Booking>;
  customers: Record<string, CustomerRecord>;
  artists: Record<string, Artist>;
  studios: Record<string, Studio>;
  pieces: Record<string, Piece>;
  waivers: Record<string, Waiver>;
  salePayments: Record<string, SalePayment>;
  deposits: Record<string, Deposit>;
  merchItems: Record<string, MerchItem>;
}

/** Derives the calendar view shape from the in-memory brainStore (offline fallback). */
export function calendarViewFromStore(studio: string, from: string, to: string, studiosCsv?: string): CalendarViewBundle {
  const state = useBrainStore.getState();
  // 'all' is the all-studios calendar (75n8): the listed studios, else every one.
  const listed = studiosCsv ? new Set(studiosCsv.split(',')) : null;
  const inScope = (id: string) => (studio === ALL_STUDIOS_SCOPE ? !listed || listed.has(id) : id === studio);

  const bookings = Object.fromEntries(
    Object.entries(state.bookings).filter(
      ([, b]) => inScope(b.studioId) && b.start >= from && b.start < to,
    ),
  );

  const customerIds = new Set(Object.values(bookings).map((b) => b.customerId));
  const customers = Object.fromEntries(
    Object.entries(state.customers).filter(([id]) => customerIds.has(id)),
  );

  const deposits = Object.fromEntries(
    Object.entries(state.deposits).filter(([, d]) => inScope(d.studioId)),
  );

  const bookingIds = new Set(Object.keys(bookings));
  const waivers = Object.fromEntries(
    Object.entries(state.waivers).filter(([, w]) => bookingIds.has(w.bookingId)),
  );
  const overrideRequests = Object.fromEntries(
    Object.entries(state.overrideRequests).filter(([, o]) => bookingIds.has(o.bookingId)),
  );

  const artists = Object.fromEntries(
    Object.entries(state.artists).filter(([, a]) => inScope(a.studioId)),
  );

  const studios = Object.fromEntries(Object.entries(state.studios).filter(([id]) => inScope(id)));

  return { bookings, customers, deposits, waivers, overrideRequests, artists, studios };
}

/**
 * Latches true the first time the real /api/views/calendar endpoint answers
 * successfully. This — not WS socket state — is what the store->cache echo
 * below gates on: the socket can be mid-reconnect (up to 30s of backoff on a
 * deploy/scale-in) while the HTTP endpoint is still perfectly reachable, so
 * "have we ever heard from the server" is the correct signal for "is the
 * query cache allowed to hold data (e.g. another user's booking) that the
 * local store doesn't know about". Never reset false once the app has
 * successfully talked to the server — a later transient fetch failure
 * shouldn't make local writes start clobbering the cache again.
 */
let serverBacked = false;

/** Test-only: reset the server-backed latch between test cases. */
export function __resetServerBacked(): void {
  serverBacked = false;
}

/**
 * Wraps a live view fetch: on success latches `serverBacked`, marks the read
 * connection online, and returns the data. On failure it falls back to the
 * in-memory store ONLY when the gate allows it (tests/standalone); in the
 * served app it marks the connection reconnecting and rethrows so react-query
 * surfaces an honest error state instead of resurrecting fixtures.
 */
async function liveFetch<T>(doFetch: () => Promise<T>, fallback: () => T): Promise<T> {
  try {
    const data = await doFetch();
    serverBacked = true;
    useConnectionStatus.getState().markOnline();
    return data;
  } catch (err) {
    if (isStoreFallbackEnabled()) return fallback();
    useConnectionStatus.getState().markReconnecting();
    throw err;
  }
}

/** GET /api/views/calendar?studio&from&to — DB-authoritative; store fallback is test-only. */
export async function fetchCalendarView(
  studio: string,
  from: string,
  to: string,
): Promise<CalendarViewBundle> {
  return liveFetch(
    async () => {
      const params = new URLSearchParams({ studio, from, to });
      const res = await fetch(`/api/views/calendar?${params.toString()}`, {
        headers: { ...authHeaders() },
      });
      if (!res.ok) throw new Error(`views/calendar ${res.status}`);
      return (await res.json()) as CalendarViewBundle;
    },
    () => calendarViewFromStore(studio, from, to),
  );
}

/**
 * GET /api/views/calendar?studio=all&studios=… — the all-studios calendar (B9 /
 * 75n8): one bundle across the persona's studios. Keyed under
 * ['calendar', 'all', from, to, studiosCsv] so the ['calendar'] prefix
 * invalidations and the ['calendar', 'all'] outbox/own-write patches reach it.
 */
export async function fetchNetworkCalendarView(studiosCsv: string, from: string, to: string): Promise<CalendarViewBundle> {
  return liveFetch(
    async () => {
      const params = new URLSearchParams({ studio: ALL_STUDIOS_SCOPE, from, to, ...(studiosCsv ? { studios: studiosCsv } : {}) });
      const res = await fetch(`/api/views/calendar?${params.toString()}`, { headers: { ...authHeaders() } });
      if (!res.ok) throw new Error(`views/calendar ${res.status}`);
      return (await res.json()) as CalendarViewBundle;
    },
    () => calendarViewFromStore(ALL_STUDIOS_SCOPE, from, to, studiosCsv),
  );
}

export function useNetworkCalendarView(studios: readonly string[], from: string, to: string) {
  const studiosCsv = [...studios].sort().join(',');
  return useQuery({
    queryKey: ['calendar', ALL_STUDIOS_SCOPE, from, to, studiosCsv],
    queryFn: () => self.fetchNetworkCalendarView(studiosCsv, from, to),
    placeholderData: isStoreFallbackEnabled()
      ? () => calendarViewFromStore(ALL_STUDIOS_SCOPE, from, to, studiosCsv)
      : undefined,
  });
}

export function useCalendarView(studio: StudioId | string | undefined, from: string, to: string) {
  return useQuery({
    queryKey: ['calendar', studio, from, to],
    queryFn: () => self.fetchCalendarView(studio as string, from, to),
    enabled: !!studio,
    // Seed the first render synchronously from the store-derived snapshot so
    // the calendar never renders empty while the live fetch is in flight. Test/
    // standalone only — the served app is DB-authoritative and shows a loading/
    // reconnecting state rather than fixtures.
    placeholderData:
      studio && isStoreFallbackEnabled()
        ? () => calendarViewFromStore(studio as string, from, to)
        : undefined,
  });
}

/** Derives the availability view shape (bookings/artists/studios only) from brainStore. */
export function availabilityViewFromStore(studio: string, from: string, to: string): AvailabilityViewBundle {
  const { bookings, artists, studios } = calendarViewFromStore(studio, from, to);
  return { bookings, artists, studios };
}

/** GET /api/views/availability?studio&from&to — derived from the calendar view's fetch/fallback. */
export async function fetchAvailabilityView(
  studio: string,
  from: string,
  to: string,
): Promise<AvailabilityViewBundle> {
  const { bookings, artists, studios } = await self.fetchCalendarView(studio, from, to);
  return { bookings, artists, studios };
}

export function useAvailabilityView(studio: StudioId | string | undefined, from: string, to: string) {
  return useQuery({
    queryKey: ['availability', studio, from, to],
    queryFn: () => self.fetchAvailabilityView(studio as string, from, to),
    enabled: !!studio,
    // Same instant-seed rationale as useCalendarView above (test/standalone only).
    placeholderData:
      studio && isStoreFallbackEnabled()
        ? () => availabilityViewFromStore(studio as string, from, to)
        : undefined,
  });
}

/**
 * The roster view (tv-ods.3): every booking + ALL artists + ALL studios,
 * network-wide and unscoped. Same {bookings, artists, studios} shape as the
 * availability view, but not windowed to a studio/date — the leaderboard and
 * the 14-day availability heatmap compute their own windows/stats client-side
 * over the full booking set. GET /api/views/roster; re-fetches only on booking
 * changes (ROSTER_VIEW_TOPICS = {bookings}).
 */
export type RosterViewBundle = AvailabilityViewBundle;

/** Derives the roster shape from brainStore (offline/test fallback): all bookings/artists/studios. */
export function rosterViewFromStore(): RosterViewBundle {
  const s = useBrainStore.getState();
  return { bookings: s.bookings, artists: s.artists, studios: s.studios };
}

/** GET /api/views/roster — DB-authoritative; store fallback is test-only. */
export async function fetchRosterView(): Promise<RosterViewBundle> {
  return liveFetch(
    async () => {
      const res = await fetch('/api/views/roster', { headers: { ...authHeaders() } });
      if (!res.ok) throw new Error(`views/roster ${res.status}`);
      return (await res.json()) as RosterViewBundle;
    },
    () => rosterViewFromStore(),
  );
}

export function useRosterView() {
  return useQuery({
    queryKey: ['roster'],
    queryFn: () => self.fetchRosterView(),
    placeholderData: isStoreFallbackEnabled() ? () => rosterViewFromStore() : undefined,
  });
}

/**
 * The messenger view (tv-ods.3): every customer + all sequence state + ALL
 * studios, network-wide and unscoped. GET /api/views/messenger; re-fetches on
 * customer/sequenceState changes.
 *
 * tv-nhs: activityFeed was removed from this view (it read the whole unbounded
 * ledger.activityFeed, growing the payload forever). The ledger table and its
 * writes stay dormant, so the feed can be re-added (windowed) later.
 */
export interface MessengerViewBundle {
  customers: Record<string, CustomerRecord>;
  sequenceState: Record<string, SequenceState>;
  studios: Record<string, Studio>;
}

/** Derives the messenger shape from brainStore (offline/test fallback): all customers/sequenceState/studios. */
export function messengerViewFromStore(): MessengerViewBundle {
  const s = useBrainStore.getState();
  return {
    customers: s.customers,
    sequenceState: s.sequenceState,
    studios: s.studios,
  };
}

/**
 * GET /api/views/customers?ids=… — the server's copy of the customers the stream
 * said changed (B2). No store fallback: without a server there is nothing newer.
 */
export async function fetchCustomersView(
  ids: readonly string[],
): Promise<{ customers: Record<string, CustomerRecord>; sequenceState?: Record<string, SequenceState> }> {
  const res = await fetch(`/api/views/customers?ids=${ids.map(encodeURIComponent).join(',')}`, {
    headers: { ...authHeaders() },
  });
  if (!res.ok) throw new Error(`views/customers ${res.status}`);
  return (await res.json()) as { customers: Record<string, CustomerRecord>; sequenceState?: Record<string, SequenceState> };
}

/** GET /api/views/messenger — DB-authoritative; store fallback is test-only. */
export async function fetchMessengerView(): Promise<MessengerViewBundle> {
  return liveFetch(
    async () => {
      const res = await fetch('/api/views/messenger', { headers: { ...authHeaders() } });
      if (!res.ok) throw new Error(`views/messenger ${res.status}`);
      return (await res.json()) as MessengerViewBundle;
    },
    () => messengerViewFromStore(),
  );
}

export function useMessengerView() {
  return useQuery({
    queryKey: ['messenger'],
    queryFn: () => self.fetchMessengerView(),
    placeholderData: isStoreFallbackEnabled() ? () => messengerViewFromStore() : undefined,
  });
}

/**
 * Collapses a studio-slug list into the query-key/query-string form used by
 * useDepositsView/fetchDepositsView: 'all' when unscoped (the Deposits Ledger
 * defaults to network-wide), else the slugs sorted + comma-joined so the same
 * set always produces the same cache key regardless of call-site ordering.
 */
function depositsStudiosKey(studios?: string[]): string {
  return studios && studios.length ? [...studios].sort().join(',') : 'all';
}

/**
 * Derives the deposits view shape from the in-memory brainStore (offline
 * fallback). Unlike the calendar, the Deposits Ledger is network-wide: with
 * no `studios` filter this returns every deposit across every studio (the
 * screen applies its own client-side studio filter), plus the bookings/
 * customers those deposits reference and ALL studios/artists — the ledger's
 * studio dropdown and per-row artist lookups need the full reference sets
 * regardless of scoping.
 */
export function depositsViewFromStore(studios?: string[]): DepositsViewBundle {
  const state = useBrainStore.getState();
  const studioSet = studios && studios.length ? new Set(studios) : undefined;

  const deposits = Object.fromEntries(
    Object.entries(state.deposits).filter(([, d]) => !studioSet || studioSet.has(d.studioId)),
  );

  // A placed deposit points at its booking by bookingId; a pending link's held
  // slot by heldBookingId (tv-2na.12.8). Both, or the missed list can't tell a
  // still-held slot from a released one.
  const bookingIds = new Set(
    Object.values(deposits)
      .flatMap((d) => [d.bookingId, d.heldBookingId])
      .filter((id): id is string => !!id),
  );
  const bookings = Object.fromEntries(Object.entries(state.bookings).filter(([id]) => bookingIds.has(id)));

  // Customers referenced by the deposits themselves (a deposit can predate
  // its booking, e.g. self-book/artist-link flows) AND by their bookings.
  const customerIds = new Set([
    ...Object.values(deposits).map((d) => d.customerId),
    ...Object.values(bookings).map((b) => b.customerId),
  ]);
  const customers = Object.fromEntries(Object.entries(state.customers).filter(([id]) => customerIds.has(id)));

  return { deposits, bookings, customers, studios: state.studios, artists: state.artists };
}

/** GET /api/views/deposits?studios=... — DB-authoritative; store fallback is test-only. */
export async function fetchDepositsView(studios?: string[]): Promise<DepositsViewBundle> {
  return liveFetch(
    async () => {
      const params = studios && studios.length ? `?studios=${encodeURIComponent(studios.join(','))}` : '';
      const res = await fetch(`/api/views/deposits${params}`, {
        headers: { ...authHeaders() },
      });
      if (!res.ok) throw new Error(`views/deposits ${res.status}`);
      return (await res.json()) as DepositsViewBundle;
    },
    () => depositsViewFromStore(studios),
  );
}

export function useDepositsView(studios?: string[]) {
  const studiosKey = depositsStudiosKey(studios);
  return useQuery({
    queryKey: ['deposits', studiosKey],
    queryFn: () => self.fetchDepositsView(studios),
    // Test/standalone only — the served app is DB-authoritative.
    placeholderData: isStoreFallbackEnabled() ? () => depositsViewFromStore(studios) : undefined,
  });
}

/**
 * Collapses a studio-slug list into the query-key/query-string form used by
 * useConfirmationsView/fetchConfirmationsView: 'all' when unscoped (empty
 * assignedStudios means "every studio in scope", same convention as
 * ConfirmationWorklist's own inScopeStudio check), else the
 * slugs sorted + comma-joined so the same set always produces the same cache
 * key regardless of call-site ordering.
 */
function confirmationsStudiosKey(studios?: string[]): string {
  return studios && studios.length ? [...studios].sort().join(',') : 'all';
}

/**
 * Derives the confirmations view shape from the in-memory brainStore (offline
 * fallback): bookings scoped to the given studios (or ALL when unscoped) +
 * their confirmationCalls (confirmationCalls carry no studioId of their own,
 * so they're scoped transitively via bookingId) + the deposits/waivers those
 * bookings reference + the customers referenced + ALL studios/artists — the
 * screens' card lookups (CustomerCard/bookingSubject) need the full reference
 * sets regardless of scoping.
 */
export function confirmationsViewFromStore(studios?: string[]): ConfirmationsViewBundle {
  const state = useBrainStore.getState();
  const studioSet = studios && studios.length ? new Set(studios) : undefined;

  const bookings = Object.fromEntries(
    Object.entries(state.bookings).filter(([, b]) => !studioSet || studioSet.has(b.studioId)),
  );
  const bookingIds = new Set(Object.keys(bookings));

  const confirmationCalls = Object.fromEntries(
    Object.entries(state.confirmationCalls).filter(([, c]) => bookingIds.has(c.bookingId)),
  );

  const deposits = Object.fromEntries(
    Object.entries(state.deposits).filter(([, d]) => !studioSet || studioSet.has(d.studioId)),
  );

  const waivers = Object.fromEntries(
    Object.entries(state.waivers).filter(([, w]) => bookingIds.has(w.bookingId)),
  );

  // Customers referenced by the in-scope bookings AND by the
  // confirmationCalls/deposits themselves (belt-and-braces, same rationale as
  // depositsViewFromStore).
  const customerIds = new Set([
    ...Object.values(bookings).map((b) => b.customerId),
    ...Object.values(confirmationCalls).map((c) => c.customerId),
    ...Object.values(deposits).map((d) => d.customerId),
  ]);
  const customers = Object.fromEntries(Object.entries(state.customers).filter(([id]) => customerIds.has(id)));

  return { bookings, confirmationCalls, customers, deposits, waivers, artists: state.artists, studios: state.studios };
}

/** GET /api/views/confirmations?studios=... — DB-authoritative; store fallback is test-only. */
export async function fetchConfirmationsView(studios?: string[]): Promise<ConfirmationsViewBundle> {
  return liveFetch(
    async () => {
      const params = studios && studios.length ? `?studios=${encodeURIComponent(studios.join(','))}` : '';
      const res = await fetch(`/api/views/confirmations${params}`, {
        headers: { ...authHeaders() },
      });
      if (!res.ok) throw new Error(`views/confirmations ${res.status}`);
      return (await res.json()) as ConfirmationsViewBundle;
    },
    () => confirmationsViewFromStore(studios),
  );
}

/**
 * `enabled` lets the LeftRail badge share this exact query (same key, same
 * data) as the Confirmations screen without fetching for other personas. The
 * app's singleton client is passed explicitly so the rail also works when
 * mounted outside a QueryClientProvider (unit tests).
 */
export function useConfirmationsView(studios?: string[], opts?: { enabled?: boolean }) {
  const studiosKey = confirmationsStudiosKey(studios);
  return useQuery(
    {
      queryKey: ['confirmations', studiosKey],
      queryFn: () => self.fetchConfirmationsView(studios),
      enabled: opts?.enabled ?? true,
      // Test/standalone only — the served app is DB-authoritative.
      placeholderData: isStoreFallbackEnabled() ? () => confirmationsViewFromStore(studios) : undefined,
    },
    queryClient,
  );
}

/**
 * Collapses a studio-slug list into the query-key/query-string form used by
 * useOpsView/fetchOpsView: 'all' when unscoped (the Ops worklist's own
 * default — an ops manager sees every studio; `buildOpsWorklist` re-applies
 * the persona's assignedStudios filter itself, item by item), else the slugs
 * sorted + comma-joined so the same set always produces the same cache key
 * regardless of call-site ordering.
 */
function opsStudiosKey(studios?: string[]): string {
  return studios && studios.length ? [...studios].sort().join(',') : 'all';
}

/**
 * Derives the Ops worklist view shape from the in-memory brainStore (offline
 * fallback): bookings scoped to the given studios (or ALL when unscoped) +
 * overrideRequests (no studioId of their own, scoped transitively via
 * bookingId) + topupPlans/noRebookFlags/payoutRecords (studio-owned, scoped
 * directly) + noDepositDeclineFlags (customer-owned, no studioId at all —
 * ALWAYS full, matching OpsOverridesAudit's own unscoped read) + the
 * customers those feeders reference + ALL studios/artists — the worklist's
 * card lookups need the full reference sets regardless of scoping.
 */
export function opsViewFromStore(studios?: string[]): OpsViewBundle {
  const state = useBrainStore.getState();
  const studioSet = studios && studios.length ? new Set(studios) : undefined;

  const bookings = Object.fromEntries(
    Object.entries(state.bookings).filter(([, b]) => !studioSet || studioSet.has(b.studioId)),
  );
  const bookingIds = new Set(Object.keys(bookings));

  const overrideRequests = Object.fromEntries(
    Object.entries(state.overrideRequests).filter(([, o]) => !studioSet || bookingIds.has(o.bookingId)),
  );
  const topupPlans = Object.fromEntries(
    Object.entries(state.topupPlans).filter(([, t]) => !studioSet || studioSet.has(t.studioId)),
  );
  const noRebookFlags = Object.fromEntries(
    Object.entries(state.noRebookFlags).filter(([, f]) => !studioSet || studioSet.has(f.studioId)),
  );
  const payoutRecords = Object.fromEntries(
    Object.entries(state.payoutRecords).filter(([, p]) => !studioSet || studioSet.has(p.studioId)),
  );
  // B1: studio-owned, scoped like payoutRecords.
  const salePayments = Object.fromEntries(
    Object.entries(state.salePayments).filter(([, sp]) => !studioSet || studioSet.has(sp.studioId)),
  );
  // No studioId column on this domain at all — always the full set.
  const noDepositDeclineFlags = state.noDepositDeclineFlags;
  // W5 mg: open unpaid links (mirrors opsView's pending/expired, unclosed, not re-sent).
  const deposits = Object.fromEntries(
    Object.entries(state.deposits).filter(
      ([, d]) =>
        (d.status === 'pending' || d.status === 'expired') &&
        !d.missedClosedAt &&
        !d.supersededByDepositId &&
        (!studioSet || studioSet.has(d.studioId)),
    ),
  );
  // W5 i5: pay-by extension requests (studio-owned).
  const paybyExtensionRequests = Object.fromEntries(
    Object.entries(state.paybyExtensionRequests ?? {}).filter(([, r]) => !studioSet || studioSet.has(r.studioId)),
  );
  // W5 mf: customers whose latest Meta (IG / FB) row is inbound — a new lead with no booking.
  const metaLeadIds = Object.values(state.customers)
    .filter((c) => (!studioSet || studioSet.has(c.primaryStudioId)) && lastUnansweredMeta(c.conversation ?? []))
    .map((c) => c.id);

  // Customers referenced by the in-scope bookings AND by topupPlans/
  // noRebookFlags/noDepositDeclineFlags directly (a topup plan or flag can
  // name a customer whose own booking fell outside this studio scope).
  const customerIds = new Set([
    ...Object.values(bookings).map((b) => b.customerId),
    ...Object.values(topupPlans).map((t) => t.customerId),
    ...Object.values(noRebookFlags).map((f) => f.customerId),
    ...Object.values(noDepositDeclineFlags).map((f) => f.customerId),
    ...Object.values(deposits).map((d) => d.customerId),
    ...Object.values(paybyExtensionRequests).map((r) => r.customerId),
    ...metaLeadIds,
  ]);
  const customers = Object.fromEntries(Object.entries(state.customers).filter(([id]) => customerIds.has(id)));

  // B8 (N8) + tv-3zjo: a pushed record or the durable marker (which outlives it).
  const pushedCustomerIds = pushedCustomerIdsOf(state.sequenceState, state.customers);
  // tv-3zjo: the customers' booked sessions at any studio, as the server sends them.
  const customerBookings = Object.values(state.bookings)
    .filter((b) => customerIds.has(b.customerId) && isCommittedBooking(b) && b.status !== 'completed')
    .map(({ customerId, status, start, durationMin, overbookable }) => ({ customerId, status, start, durationMin, overbookable }));
  return {
    pushedCustomerIds,
    customerBookings,
    bookings,
    overrideRequests,
    topupPlans,
    noRebookFlags,
    noDepositDeclineFlags,
    payoutRecords,
    customers,
    deposits,
    paybyExtensionRequests,
    salePayments,
    artists: state.artists,
    studios: state.studios,
  };
}

/** GET /api/views/ops?studios=... — DB-authoritative; store fallback is test-only. */
export async function fetchOpsView(studios?: string[]): Promise<OpsViewBundle> {
  return liveFetch(
    async () => {
      const params = studios && studios.length ? `?studios=${encodeURIComponent(studios.join(','))}` : '';
      const res = await fetch(`/api/views/ops${params}`, {
        headers: { ...authHeaders() },
      });
      if (!res.ok) throw new Error(`views/ops ${res.status}`);
      return (await res.json()) as OpsViewBundle;
    },
    () => opsViewFromStore(studios),
  );
}

export function useOpsView(studios?: string[], opts?: { enabled?: boolean }) {
  const studiosKey = opsStudiosKey(studios);
  // 7kta: the rail's Ops badge shares this query with the Ops screen, so it
  // takes `enabled` (off for other personas) and the app's queryClient
  // directly (the rail renders outside some test providers), like
  // useConfirmationsView.
  return useQuery(
    {
      queryKey: ['ops', studiosKey],
      queryFn: () => self.fetchOpsView(studios),
      enabled: opts?.enabled ?? true,
      // Test/standalone only — the served app is DB-authoritative.
      placeholderData: isStoreFallbackEnabled() ? () => opsViewFromStore(studios) : undefined,
    },
    queryClient,
  );
}

/**
 * Collapses a studio-slug list into the query-key/query-string form used by
 * useFloorView/fetchFloorView: 'all' when unscoped (the Floor desk's own
 * "all studios" default — FloorManager re-applies the persona's
 * assignedStudios filter itself, same convention as the Ops worklist), else
 * the slugs sorted + comma-joined so the same set always produces the same
 * cache key regardless of call-site ordering.
 */
function floorStudiosKey(studios?: string[]): string {
  return studios && studios.length ? [...studios].sort().join(',') : 'all';
}

/**
 * Derives the Floor desk view shape from the in-memory brainStore (offline
 * fallback): bookings scoped to the given studios (or ALL when unscoped) +
 * the pieces/deposits they reference via booking.pieceId/depositId (neither
 * carries a studioId column of its own, so — like overrideRequests via
 * bookingId elsewhere — scoping is transitive) + waivers scoped transitively
 * via bookingId + salePayments/merchItems (studio-owned, scoped directly) +
 * the customers those bookings/deposits reference + ALL studios/artists (the
 * studio-filter dropdown and every row's artist lookup need the full
 * reference sets regardless of scoping).
 */
export function floorViewFromStore(studios?: string[]): FloorViewBundle {
  const state = useBrainStore.getState();
  const studioSet = studios && studios.length ? new Set(studios) : undefined;

  const bookings = Object.fromEntries(
    Object.entries(state.bookings).filter(([, b]) => !studioSet || studioSet.has(b.studioId)),
  );
  const bookingIds = new Set(Object.keys(bookings));

  const pieceIds = new Set(
    Object.values(bookings)
      .map((b) => b.pieceId)
      .filter((id): id is string => !!id),
  );
  const pieces = Object.fromEntries(Object.entries(state.pieces).filter(([id]) => pieceIds.has(id)));

  const depositIds = new Set(
    Object.values(bookings)
      .map((b) => b.depositId)
      .filter((id): id is string => !!id),
  );
  // calendar-11 (a): also a hold's deposit found only by its slot (heldBookingId).
  const deposits = Object.fromEntries(
    Object.entries(state.deposits).filter(
      ([id, d]) => depositIds.has(id) || (!!d.heldBookingId && bookingIds.has(d.heldBookingId)),
    ),
  );

  const waivers = Object.fromEntries(
    Object.entries(state.waivers).filter(([, w]) => bookingIds.has(w.bookingId)),
  );

  const salePayments = Object.fromEntries(
    Object.entries(state.salePayments).filter(([, s]) => !studioSet || studioSet.has(s.studioId)),
  );
  const merchItems = Object.fromEntries(
    Object.entries(state.merchItems).filter(([, m]) => !studioSet || studioSet.has(m.studioId)),
  );

  // Customers referenced by the in-scope bookings AND by the deposits
  // themselves (belt-and-braces, same rationale as opsView/depositsView).
  const customerIds = new Set([
    ...Object.values(bookings).map((b) => b.customerId),
    ...Object.values(deposits).map((d) => d.customerId),
  ]);
  const customers = Object.fromEntries(Object.entries(state.customers).filter(([id]) => customerIds.has(id)));

  return {
    bookings,
    customers,
    artists: state.artists,
    studios: state.studios,
    pieces,
    waivers,
    salePayments,
    deposits,
    merchItems,
  };
}

/** GET /api/views/floor?studios=... — DB-authoritative; store fallback is test-only. */
export async function fetchFloorView(studios?: string[]): Promise<FloorViewBundle> {
  return liveFetch(
    async () => {
      const params = studios && studios.length ? `?studios=${encodeURIComponent(studios.join(','))}` : '';
      const res = await fetch(`/api/views/floor${params}`, {
        headers: { ...authHeaders() },
      });
      if (!res.ok) throw new Error(`views/floor ${res.status}`);
      return (await res.json()) as FloorViewBundle;
    },
    () => floorViewFromStore(studios),
  );
}

export function useFloorView(studios?: string[]) {
  const studiosKey = floorStudiosKey(studios);
  return useQuery({
    queryKey: ['floor', studiosKey],
    queryFn: () => self.fetchFloorView(studios),
    // Test/standalone only — the served app is DB-authoritative.
    placeholderData: isStoreFallbackEnabled() ? () => floorViewFromStore(studios) : undefined,
  });
}

/**
 * Local echo: reacts to brainStore writes for the calendar-relevant slices
 * so already-cached calendar/availability queries stay in sync with LOCAL
 * store changes that don't originate from this screen's own optimistic-write
 * call sites (e.g. a waiver signed elsewhere in the app).
 *
 * Gated on `serverBacked` (has the real endpoint ever answered), NOT on the
 * `/ws` socket being OPEN right now:
 *
 * - SERVER-BACKED: the query cache can already hold data that came from the
 *   LIVE endpoint, including bookings from *other users* that brainStore
 *   (bootstrap snapshot + this user's own writes only) never learns about.
 *   Overwriting the cache with `calendarViewFromStore` here would silently
 *   drop those. Gating on live WS connectivity instead of this was wrong:
 *   the socket can be mid-reconnect (up to 30s of backoff on a deploy/
 *   scale-in) while the HTTP endpoint is still reachable, so a plain local
 *   write during that window would have clobbered the cache anyway.
 *   Invalidating here was ALSO wrong even while genuinely connected: it
 *   races this user's own optimistic write's outbox-driven invalidation
 *   (Task 3/5) against the in-flight PUT, flickering the booking out. So
 *   server-backed, this echo does nothing at all — the outbox WS
 *   invalidation (api/liveStream.ts) plus each mutation's own optimistic
 *   `setQueryData` already own reactivity; every calendar-relevant entity
 *   write emits an outbox event, so there is nothing left for a blanket
 *   local-store listener to usefully do without risking exactly this kind
 *   of clobber/flicker.
 * - NOT server-backed (truly offline/standalone/tests — `fetchCalendarView`'s
 *   own fallback IS `calendarViewFromStore`): there is no other user's data
 *   to lose, since the store and the endpoint's fallback read the exact same
 *   place. Recomputing synchronously here is what makes the UI instantly
 *   reactive with no server round-trip at all (see the reactive-waiver test).
 *
 * Registered once at module load; the shared `queryClient` is a
 * process-lifetime singleton so there's nothing to unsubscribe.
 */
useBrainStore.subscribe((state, prev) => {
  // The store->cache echo is a test/standalone affordance only. In the served
  // app the endpoint + outbox WS own reactivity, so never echo store data.
  if (!isStoreFallbackEnabled()) return;
  if (
    state.bookings === prev.bookings &&
    state.customers === prev.customers &&
    state.deposits === prev.deposits &&
    state.waivers === prev.waivers &&
    state.overrideRequests === prev.overrideRequests &&
    state.artists === prev.artists &&
    state.studios === prev.studios &&
    state.confirmationCalls === prev.confirmationCalls &&
    state.topupPlans === prev.topupPlans &&
    state.noRebookFlags === prev.noRebookFlags &&
    state.payoutRecords === prev.payoutRecords &&
    state.noDepositDeclineFlags === prev.noDepositDeclineFlags &&
    state.pieces === prev.pieces &&
    state.salePayments === prev.salePayments &&
    state.merchItems === prev.merchItems &&
    state.sequenceState === prev.sequenceState
  ) {
    return;
  }

  if (serverBacked) {
    // The endpoint (+ outbox WS + each mutation's own optimistic write)
    // already owns reactivity — do nothing so we never clobber/flicker
    // live, server-fetched data with a recompute from the local store.
    return;
  }

  for (const query of queryClient.getQueryCache().findAll({ queryKey: ['calendar'] })) {
    const [, studio, from, to, studiosCsv] = query.queryKey as [string, string, string, string, string | undefined];
    if (typeof studio === 'string' && typeof from === 'string' && typeof to === 'string') {
      queryClient.setQueryData(query.queryKey, calendarViewFromStore(studio, from, to, studiosCsv));
    }
  }
  for (const query of queryClient.getQueryCache().findAll({ queryKey: ['availability'] })) {
    const [, studio, from, to] = query.queryKey as [string, string, string, string];
    if (typeof studio === 'string' && typeof from === 'string' && typeof to === 'string') {
      queryClient.setQueryData(query.queryKey, availabilityViewFromStore(studio, from, to));
    }
  }
  for (const query of queryClient.getQueryCache().findAll({ queryKey: ['deposits'] })) {
    const [, studiosKey] = query.queryKey as [string, string];
    if (typeof studiosKey === 'string') {
      const studios = studiosKey === 'all' ? undefined : studiosKey.split(',');
      queryClient.setQueryData(query.queryKey, depositsViewFromStore(studios));
    }
  }
  for (const query of queryClient.getQueryCache().findAll({ queryKey: ['confirmations'] })) {
    const [, studiosKey] = query.queryKey as [string, string];
    if (typeof studiosKey === 'string') {
      const studios = studiosKey === 'all' ? undefined : studiosKey.split(',');
      queryClient.setQueryData(query.queryKey, confirmationsViewFromStore(studios));
    }
  }
  for (const query of queryClient.getQueryCache().findAll({ queryKey: ['ops'] })) {
    const [, studiosKey] = query.queryKey as [string, string];
    if (typeof studiosKey === 'string') {
      const studios = studiosKey === 'all' ? undefined : studiosKey.split(',');
      queryClient.setQueryData(query.queryKey, opsViewFromStore(studios));
    }
  }
  for (const query of queryClient.getQueryCache().findAll({ queryKey: ['floor'] })) {
    const [, studiosKey] = query.queryKey as [string, string];
    if (typeof studiosKey === 'string') {
      const studios = studiosKey === 'all' ? undefined : studiosKey.split(',');
      queryClient.setQueryData(query.queryKey, floorViewFromStore(studios));
    }
  }
  // Roster is unscoped (no key params) — one entry to refresh.
  for (const query of queryClient.getQueryCache().findAll({ queryKey: ['roster'] })) {
    queryClient.setQueryData(query.queryKey, rosterViewFromStore());
  }
  // Messenger is unscoped (no key params) — one entry to refresh.
  for (const query of queryClient.getQueryCache().findAll({ queryKey: ['messenger'] })) {
    queryClient.setQueryData(query.queryKey, messengerViewFromStore());
  }
});
